'use client';

import { ComponentProps, Dispatch, SetStateAction, useState } from 'react';
import MetaNavigation from './meta-navigation';
import Flyout, { FooterNavigationItem } from './flyout';
import ScrollToTopButton from './scroll-to-top-button';
import FooterGrid from './footer-grid';
import { EventClickArea } from '@tuic/tracking';
import { FooterCookieSettingsOpener } from './FooterCookieSettingsOpener';
import { Locale } from '@tuic/i18n';
import { I18NFromComponent } from '../../../utils';
import AppStoreRating from './app-store-rating';

export type FooterProps = {
  meta: ComponentProps<typeof MetaNavigation>;
  items: FooterNavigationItem[];
  locale?: Locale;
  i18n?: I18NFromComponent<typeof AppStoreRating>;
};

const createFlyoutToggle = (
  activeFlyout: undefined | number,
  setActiveFlyout: Dispatch<SetStateAction<number | undefined>>,
  idx: number,
) => {
  if (activeFlyout === idx) {
    return () => {
      setActiveFlyout(undefined);
    };
  }

  return () => setActiveFlyout(idx);
};

const Footer = ({ meta, items, locale, i18n }: FooterProps) => {
  const [activeFlyout, setActiveFlyout] = useState<number | undefined>(
    undefined,
  );

  if (!meta || !items) return null;

  return (
    <footer
      data-track-click-area={EventClickArea.Footer}
      className="bg-primary-ocean-100 lg:py-[3.75rem] [&_a]:no-underline"
    >
      <nav className="relative mx-auto my-0 flex max-w-[1200px] flex-col px-0 leading-[1.4] text-secondary-marine-100 lg:px-[3.125rem] xl:max-w-[1300px] xl:px-[4.375rem]">
        <FooterGrid>
          <FooterGrid.MetaSegment className={'block lg:hidden'}>
            <MetaNavigation locale={locale} {...meta} i18n={i18n} />
          </FooterGrid.MetaSegment>

          {items.map((item, idx) => (
            <FooterGrid.LinkCollectionSegment key={item.title}>
              <Flyout
                isActive={activeFlyout === idx}
                handleToggle={createFlyoutToggle(
                  activeFlyout,
                  setActiveFlyout,
                  idx,
                )}
                item={item}
              />
            </FooterGrid.LinkCollectionSegment>
          ))}

          <FooterGrid.MetaSegment className={'hidden lg:block'}>
            <MetaNavigation locale={locale} {...meta} />
          </FooterGrid.MetaSegment>

          <FooterGrid.ScrollToTopSegment>
            <ScrollToTopButton />
          </FooterGrid.ScrollToTopSegment>
        </FooterGrid>
      </nav>
      <FooterCookieSettingsOpener />
    </footer>
  );
};

export { Footer };
